import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareQuote, Plus, ArrowRight, Loader2, Inbox, Send, EyeOff, User } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ThemesButton } from "@/components/AIAssistants";

const STATUS_META = {
  pending: { label: "Pending", style: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Completed", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  declined: { label: "Declined", style: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function Feedback360() {
  const { user } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondReq, setRespondReq] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/feedback360/requests/inbox"),
        api.get("/feedback360/requests/sent"),
        api.get("/feedback360/received"),
      ]);
      setInbox(a.data.items || []);
      setSent(b.data.items || []);
      setReceived(c.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (!user?.employee_id) {
    return <div className="axia-card p-8 text-sm text-slate-500">Your account isn't linked to an employee record yet.</div>;
  }

  return (
    <div className="max-w-5xl space-y-6" data-testid="feedback360-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Performance · 360</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Peer feedback</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Ask peers for input. Anonymous by default — peer identity is redacted on read for the recipient.</p>
        </div>
        <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
          <DialogTrigger asChild>
            <Button data-testid="request-feedback-btn"><Plus className="w-4 h-4 mr-2" /> Request feedback</Button>
          </DialogTrigger>
          <RequestDialog onClose={() => { setRequestOpen(false); reload(); }} />
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Inbox className="w-3.5 h-3.5 mr-1.5" /> To fill ({inbox.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Requests sent ({sent.length})
          </TabsTrigger>
          <TabsTrigger value="received" data-testid="tab-received">
            <MessageSquareQuote className="w-3.5 h-3.5 mr-1.5" /> Received ({received.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="pt-4 space-y-3">
          {loading ? <div className="text-sm text-slate-500">Loading…</div>
          : inbox.length === 0 ? <Empty msg="No requests waiting on you." />
          : inbox.map((req) => (
            <button
              key={req.id}
              onClick={() => setRespondReq(req)}
              className="w-full text-left axia-card p-5 hover:border-slate-400 transition flex items-center gap-4"
              data-testid={`inbox-row-${req.id}`}
            >
              <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                {(req.subject_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-900">Feedback for {req.subject_name}</div>
                <div className="text-xs text-slate-500">{req.subject_designation} · {req.questions.length} questions</div>
              </div>
              <span className={`axia-pill ${STATUS_META.pending.style}`}>Pending</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </button>
          ))}
        </TabsContent>

        <TabsContent value="sent" className="pt-4 space-y-3">
          {sent.length === 0 ? <Empty msg="You haven't requested feedback yet." />
          : sent.map((req) => {
            const m = STATUS_META[req.status] || STATUS_META.pending;
            return (
              <div key={req.id} className="axia-card p-4 flex items-center gap-4" data-testid={`sent-row-${req.id}`}>
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                  {(req.peer_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{req.peer_name}</div>
                  <div className="text-xs text-slate-500">{req.anonymity === "anonymous" ? "anonymous" : "named"}</div>
                </div>
                <span className={`axia-pill ${m.style}`}>{m.label}</span>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="received" className="pt-4 space-y-3">
          {received.length > 1 && (
            <div className="flex items-center justify-between axia-card p-3">
              <div className="text-xs text-slate-500">
                {received.length} responses received. Use AI to extract patterns without revealing identities.
              </div>
              <ThemesButton employeeId={user.employee_id} count={received.length} />
            </div>
          )}
          {received.length === 0 ? <Empty msg="No feedback received yet." />
          : received.map((r) => <ReceivedCard key={r.id} response={r} />)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!respondReq} onOpenChange={(o) => !o && setRespondReq(null)}>
        <DialogContent className="max-w-2xl">
          {respondReq && <RespondForm req={respondReq} onClose={() => { setRespondReq(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="axia-card p-12 text-center text-slate-500">
      <MessageSquareQuote className="w-6 h-6 mx-auto text-slate-300" />
      <p className="text-sm mt-3">{msg}</p>
    </div>
  );
}

function ReceivedCard({ response }) {
  let ago = "";
  try { ago = formatDistanceToNow(parseISO(response.submitted_at), { addSuffix: true }); } catch {}
  const isAnon = response.anonymity === "anonymous" || response.peer_name === "Anonymous";
  return (
    <article className="axia-card p-5" data-testid={`received-${response.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${isAnon ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-white"}`}>
          {isAnon ? <EyeOff className="w-4 h-4" /> : (response.peer_name || "").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-900">{response.peer_name}</div>
          <div className="text-[11px] text-slate-500">{ago}</div>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {(response.answers || []).map((a, i) => (
          <div key={i}>
            <div className="axia-overline">{a.question}</div>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{a.answer}</p>
          </div>
        ))}
        {response.overall_strengths && (
          <div>
            <div className="axia-overline text-emerald-700">Strengths</div>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{response.overall_strengths}</p>
          </div>
        )}
        {response.overall_growth_areas && (
          <div>
            <div className="axia-overline text-amber-700">Growth areas</div>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{response.overall_growth_areas}</p>
          </div>
        )}
      </div>
    </article>
  );
}

function RequestDialog({ onClose }) {
  const [employees, setEmployees] = useState([]);
  const [peerIds, setPeerIds] = useState([]);
  const [anonymity, setAnonymity] = useState("anonymous");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/employees", { params: { limit: 200 } }).then((r) => setEmployees((r.data.items || []).filter((e) => e.id !== user?.employee_id)));
  }, [user?.employee_id]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return employees.filter((e) => !q || `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) || e.designation?.toLowerCase().includes(q));
  }, [employees, search]);

  const toggle = (id) => {
    setPeerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    if (peerIds.length === 0) return toast.error("Pick at least one peer");
    setSaving(true);
    try {
      const r = await api.post("/feedback360/requests", {
        subject_id: user.employee_id,
        peer_ids: peerIds,
        anonymity,
      });
      toast.success(`Sent ${r.data.created} requests`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="request-dialog">
      <DialogHeader>
        <DialogTitle>Request peer feedback</DialogTitle>
        <DialogDescription>Pick peers who've worked closely with you. They'll get a 3-question survey.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search peers…" data-testid="peer-search" />
        <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto">
          {filtered.slice(0, 30).map((e) => {
            const sel = peerIds.includes(e.id);
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => toggle(e.id)}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50 ${sel ? "bg-slate-100" : ""}`}
                data-testid={`peer-${e.id}`}
              >
                <input type="checkbox" checked={sel} readOnly className="accent-slate-900" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{e.first_name} {e.last_name}</div>
                  <div className="text-xs text-slate-500">{e.designation} · {e.department}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div>
          <Label>Anonymity</Label>
          <Select value={anonymity} onValueChange={setAnonymity}>
            <SelectTrigger data-testid="anonymity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anonymous">Anonymous — peer identity hidden from you</SelectItem>
              <SelectItem value="named">Named — peer identity visible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="request-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Send {peerIds.length || ""} request{peerIds.length === 1 ? "" : "s"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function RespondForm({ req, onClose }) {
  const [answers, setAnswers] = useState(req.questions.map((q) => ({ question: q, answer: "" })));
  const [strengths, setStrengths] = useState("");
  const [growth, setGrowth] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (answers.some((a) => !a.answer.trim())) return toast.error("Answer all questions");
    setSaving(true);
    try {
      await api.post(`/feedback360/requests/${req.id}/respond`, {
        answers,
        overall_strengths: strengths || null,
        overall_growth_areas: growth || null,
      });
      toast.success("Feedback submitted");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const decline = async () => {
    await api.post(`/feedback360/requests/${req.id}/decline`);
    toast.success("Declined");
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Feedback for {req.subject_name}</DialogTitle>
        <DialogDescription>{req.anonymity === "anonymous" ? "Your identity will not be shown to the recipient." : "Your name will be shown to the recipient."}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {answers.map((a, i) => (
          <div key={i}>
            <Label>{a.question}</Label>
            <Textarea
              value={a.answer}
              onChange={(e) => {
                const next = [...answers];
                next[i] = { ...a, answer: e.target.value };
                setAnswers(next);
              }}
              rows={3}
              data-testid={`answer-${i}`}
            />
          </div>
        ))}
        <div>
          <Label>Strengths (optional)</Label>
          <Textarea value={strengths} onChange={(e) => setStrengths(e.target.value)} rows={2} data-testid="strengths" />
        </div>
        <div>
          <Label>Growth areas (optional)</Label>
          <Textarea value={growth} onChange={(e) => setGrowth(e.target.value)} rows={2} data-testid="growth" />
        </div>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={decline} className="text-slate-500" data-testid="respond-decline">Decline</Button>
        <Button onClick={submit} disabled={saving} data-testid="respond-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Submit feedback
        </Button>
      </DialogFooter>
    </>
  );
}
